import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { ImportPreviewResponse } from '../../types/importTypes';
import { ImportPreviewTable } from './ImportPreviewTable';
import styles from '../../styles/Modal.module.css';

interface ImportPreviewModalProps {
  preview: ImportPreviewResponse;
  creativeTypeName: string;
  onClose: () => void;
  onCommit: () => Promise<void>;
}

export const ImportPreviewModal: React.FC<ImportPreviewModalProps> = ({
  preview,
  creativeTypeName,
  onClose,
  onCommit,
}) => {
  const [isCommitting, setIsCommitting] = useState(false);

  const handleCommit = async () => {
    setIsCommitting(true);
    try {
      await onCommit();
    } finally {
      setIsCommitting(false);
    }
  };

  const { summary } = preview;
  const hasErrors = summary.rows_with_errors > 0;
  const hasWarnings = summary.rows_with_warnings > 0;
  const canCommit = summary.valid_rows > 0;

  return ReactDOM.createPortal(
    <div className={styles.modalOverlay} data-modal-root="true">
      <div className={styles.modalContent} style={{ maxWidth: 900, maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
        <h2 style={{ marginTop: 0, marginBottom: 16 }}>
          Import Preview: {creativeTypeName}
        </h2>

        {/* Summary bar */}
        <div style={{
          display: 'flex',
          gap: 24,
          padding: '12px 16px',
          backgroundColor: '#f9fafb',
          borderRadius: 8,
          marginBottom: 16,
          flexWrap: 'wrap',
        }}>
          <div>
            <span style={{ fontWeight: 600, marginRight: 4 }}>{summary.total_rows}</span>
            <span style={{ color: '#6b7280' }}>total rows</span>
          </div>
          <div style={{ color: '#059669' }}>
            <span style={{ fontWeight: 600, marginRight: 4 }}>{summary.valid_rows}</span>
            <span>valid</span>
          </div>
          {hasErrors && (
            <div style={{ color: '#dc2626' }}>
              <span style={{ fontWeight: 600, marginRight: 4 }}>{summary.rows_with_errors}</span>
              <span>with errors</span>
            </div>
          )}
          {hasWarnings && (
            <div style={{ color: '#d97706' }}>
              <span style={{ fontWeight: 600, marginRight: 4 }}>{summary.rows_with_warnings}</span>
              <span>with warnings</span>
            </div>
          )}
        </div>

        {/* Table container with scroll */}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', marginBottom: 16 }}>
          <ImportPreviewTable rows={preview.rows} />
        </div>

        {/* Footer with actions */}
        <div className={styles.modalActions}>
          {hasErrors && (
            <span style={{ color: '#6b7280', fontSize: '0.875rem', marginRight: 'auto' }}>
              {summary.rows_with_errors} row{summary.rows_with_errors !== 1 ? 's' : ''} with errors will be skipped
            </span>
          )}
          <button
            onClick={onClose}
            disabled={isCommitting}
            className={styles.buttonSecondary}
          >
            Cancel
          </button>
          <button
            onClick={handleCommit}
            disabled={isCommitting || !canCommit}
            className={styles.buttonPrimary}
          >
            {isCommitting ? 'Creating...' : `Create ${summary.valid_rows} Item${summary.valid_rows !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
